/* eslint-env browser */
import React, { useState, useCallback, useEffect } from 'react';
import Recaptcha from 'react-recaptcha';
import usePageMeta from '../hooks/usePageMeta';
import { submitContactForm } from '../api/contact';
import { CtaButton } from '../components/landing/ui/cta-button';

type FormStatus = 'idle' | 'submitting' | 'success' | 'error';

interface ContactFormFields {
  name: string;
  email: string;
  phone: string;
  message: string;
}

const INITIAL_FORM: ContactFormFields = { name: '', email: '', phone: '', message: '' };

const ContactUs: React.FC = () => {
  usePageMeta({
    title: 'Contact Us — BoostHiring',
    description:
      'Get in touch with the BoostHiring team. We would love to hear from you about recruitment solutions, partnership opportunities, or any questions.',
    canonical: 'https://boosthiring.ca/contact',
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [form, setForm] = useState<ContactFormFields>(INITIAL_FORM);
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);
  const [status, setStatus] = useState<FormStatus>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (status === 'success') {
      window.scrollTo(0, 0);
    }
  }, [status]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const { name, value } = e.target;
      setForm(prev => ({ ...prev, [name]: value }));
    },
    []
  );

  const verifyRecaptcha = useCallback((response: string) => {
    setRecaptchaToken(response);
  }, []);

  const expiredRecaptcha = useCallback(() => {
    setRecaptchaToken(null);
  }, []);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (status === 'submitting') return;
      setErrorMessage('');

      if (!recaptchaToken) {
        setErrorMessage('Please verify that you are not a robot.');
        return;
      }

      setStatus('submitting');

      try {
        await submitContactForm({
          name: form.name.trim(),
          email: form.email.trim(),
          phone: form.phone.trim() || undefined,
          message: form.message.trim(),
          recaptchaToken,
        });
        setStatus('success');
        setForm(INITIAL_FORM);
        setRecaptchaToken(null);
      } catch (err) {
        setStatus('error');
        setRecaptchaToken(null);
        if ((window as any).grecaptcha) {
          (window as any).grecaptcha.reset();
        }
        const serverMessage =
          (err as any).response && (err as any).response.data && (err as any).response.data.error;
        setErrorMessage(serverMessage || 'Something went wrong. Please try again.');
      }
    },
    [form, recaptchaToken, status]
  );

  const handleReset = useCallback(() => {
    setStatus('idle');
    setErrorMessage('');
  }, []);

  // ── Success State ──────────────────────────────────────────────────────
  if (status === 'success') {
    return (
      <main className="min-h-screen bg-bg-muted">
        <div className="container mx-auto px-6 py-16 max-w-2xl text-center">
          <div className="bg-white rounded-2xl shadow-lg p-12 border border-border-muted">
            <div className="w-16 h-16 bg-tertiary bg-opacity-10 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg
                aria-hidden="true"
                className="w-8 h-8 text-tertiary"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <h2 className="text-3xl font-bold text-primary mb-4">Message Sent!</h2>
            <p className="text-slate-600 text-lg mb-8 leading-relaxed">
              Thank you for reaching out. We&apos;ve received your message and will get back to you
              as soon as possible. A confirmation email has been sent to your inbox.
            </p>
            <CtaButton as="link" to="/">
              Back to Home
            </CtaButton>
          </div>
        </div>
      </main>
    );
  }

  // ── Form State ─────────────────────────────────────────────────────────
  return (
    <main className="min-h-screen bg-bg-muted">
      <div className="container mx-auto px-6 py-16 max-w-2xl">
        <div className="bg-white rounded-2xl shadow-lg p-8 md:p-10 border border-border-muted">
          <h1 className="text-3xl md:text-4xl font-bold text-primary mb-2">Contact Us</h1>
          <p className="text-slate-600 mb-8">
            Have a question or want to learn more? Fill out the form and we&apos;ll be in touch.
          </p>

          {/* Error banner */}
          {errorMessage && (
            <div
              role="alert"
              className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-start gap-3"
            >
              <svg
                aria-hidden="true"
                className="w-5 h-5 flex-shrink-0 mt-0.5"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
              <span>{errorMessage}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Name */}
            <div>
              <label
                htmlFor="contact-name"
                className="block text-sm font-semibold text-primary mb-2"
              >
                Full Name <span className="text-coral">*</span>
              </label>
              <input
                id="contact-name"
                name="name"
                type="text"
                autoComplete="name"
                required
                maxLength={200}
                value={form.name}
                onChange={handleChange}
                disabled={status === 'submitting'}
                placeholder="Jane Smith"
                className="w-full px-4 py-3 rounded-lg border border-border-muted focus:outline-none focus:ring-2 focus:ring-tertiary focus:border-transparent transition-shadow text-slate-800 placeholder-slate-400 disabled:opacity-50"
              />
            </div>

            {/* Email */}
            <div>
              <label
                htmlFor="contact-email"
                className="block text-sm font-semibold text-primary mb-2"
              >
                Email Address <span className="text-coral">*</span>
              </label>
              <input
                id="contact-email"
                name="email"
                type="email"
                autoComplete="email"
                required
                maxLength={320}
                value={form.email}
                onChange={handleChange}
                disabled={status === 'submitting'}
                placeholder="jane@company.com"
                className="w-full px-4 py-3 rounded-lg border border-border-muted focus:outline-none focus:ring-2 focus:ring-tertiary focus:border-transparent transition-shadow text-slate-800 placeholder-slate-400 disabled:opacity-50"
              />
            </div>

            {/* Phone */}
            <div>
              <label
                htmlFor="contact-phone"
                className="block text-sm font-semibold text-primary mb-2"
              >
                Phone Number <span className="text-slate-400 font-normal">(optional)</span>
              </label>
              <input
                id="contact-phone"
                name="phone"
                type="tel"
                autoComplete="tel"
                maxLength={30}
                value={form.phone}
                onChange={handleChange}
                disabled={status === 'submitting'}
                placeholder="+1 (555) 000-0000"
                className="w-full px-4 py-3 rounded-lg border border-border-muted focus:outline-none focus:ring-2 focus:ring-tertiary focus:border-transparent transition-shadow text-slate-800 placeholder-slate-400 disabled:opacity-50"
              />
            </div>

            {/* Message */}
            <div>
              <label
                htmlFor="contact-message"
                className="block text-sm font-semibold text-primary mb-2"
              >
                Message <span className="text-coral">*</span>
              </label>
              <textarea
                id="contact-message"
                name="message"
                required
                maxLength={5000}
                rows={5}
                value={form.message}
                onChange={handleChange}
                disabled={status === 'submitting'}
                placeholder="Tell us how we can help..."
                className="w-full px-4 py-3 rounded-lg border border-border-muted focus:outline-none focus:ring-2 focus:ring-tertiary focus:border-transparent transition-shadow text-slate-800 placeholder-slate-400 resize-vertical disabled:opacity-50"
              />
            </div>

            {/* reCAPTCHA */}
            <div>
              <Recaptcha
                sitekey="6LcFT8wsAAAAANJr9ZGEuKw-vJ3n-_wjfbHpYzkf"
                render="explicit"
                verifyCallback={verifyRecaptcha}
                expiredCallback={expiredRecaptcha}
              />
            </div>

            {/* Submit */}
            <CtaButton
              as="button"
              type="submit"
              disabled={status === 'submitting'}
              className="w-full"
            >
              {status === 'submitting' ? (
                <>
                  <svg
                    aria-hidden="true"
                    className="animate-spin h-5 w-5"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  Sending...
                </>
              ) : (
                'Send Message'
              )}
            </CtaButton>

            {/* Retry link for error state */}
            {status === 'error' && (
              <button
                type="button"
                onClick={handleReset}
                className="w-full text-center text-sm text-tertiary hover:underline font-medium"
              >
                Try again
              </button>
            )}
          </form>
        </div>
      </div>
    </main>
  );
};

export default ContactUs;
